import { useEffect, useState } from "react";
import { Gavel, Repeat, Tag, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Feb-2026 — In-place Konvertierung eines bestehenden Festpreis-Inserats:
 *   • Festpreis → Auktion (mit Startpreis, Sofortkauf, Auktionsende)
 *   • Festpreis → Sharing (Tagesrate, min/max Tage, Kaution, Übergabeort)
 *   • zurück zu Festpreis
 *
 * Öffnet ein Modal über dem Dashboard, ohne dass der Händler in den
 * vollständigen Edit-Modus wechseln muss.
 */
const TARGETS = [
    { key: "auction", label: "Auktion", icon: Gavel, desc: "Höchstbietender gewinnt — Startpreis & Auktionsende festlegen.", role: "b2b" },
    { key: "sharing", label: "Sharing / Vermietung", icon: Repeat, desc: "Fahrzeug zusätzlich tageweise vermieten." },
    { key: "fixed", label: "Festpreis (zurücksetzen)", icon: Tag, desc: "Auktion/Sharing entfernen — wieder reiner Festpreis." },
];

const inFutureDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 16); // for datetime-local
};

export default function ConvertListingDialog({ listing, currentUser, onClose, onSuccess, initialTarget }) {
    const isDealer = currentUser?.role === "b2b" || currentUser?.role === "admin";
    const computedInitial = listing.sale_type === "auction" ? "fixed" : "auction";
    const [target, setTarget] = useState(initialTarget || (isDealer ? computedInitial : "sharing"));
    const [submitting, setSubmitting] = useState(false);
    // Auction fields
    const [startingPrice, setStartingPrice] = useState(Math.max(1, Math.round((listing.price || 100) * 0.7)));
    const [buyNowPrice, setBuyNowPrice] = useState(listing.price || "");
    const [auctionEnd, setAuctionEnd] = useState(inFutureDays(7));
    // Sharing fields
    const [dailyRate, setDailyRate] = useState(listing.sharing_daily_rate || 49);
    const [minDays, setMinDays] = useState(listing.sharing_min_days || 1);
    const [maxDays, setMaxDays] = useState(listing.sharing_max_days || 14);
    const [deposit, setDeposit] = useState(listing.sharing_deposit || 250);
    // Feb-2026: Smart-Empfehlung
    const [recommendation, setRecommendation] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get(`/listings/${listing.id}/convert/recommendation`)
            .then((r) => { if (!cancelled) setRecommendation(r.data); })
            .catch(() => { /* ignore — empfehlung ist optional */ });
        return () => { cancelled = true; };
    }, [listing.id]);

    const applyRecommendation = () => {
        if (!recommendation || !recommendation.recommended_target) return;
        const t = recommendation.recommended_target;
        setTarget(t);
        const s = recommendation.suggested || {};
        if (t === "auction") {
            if (s.starting_price != null) setStartingPrice(s.starting_price);
            if (s.buy_now_price != null) setBuyNowPrice(s.buy_now_price);
            if (s.auction_end) setAuctionEnd(new Date(s.auction_end).toISOString().slice(0, 16));
        } else if (t === "sharing") {
            if (s.sharing_daily_rate != null) setDailyRate(s.sharing_daily_rate);
            if (s.sharing_min_days != null) setMinDays(s.sharing_min_days);
            if (s.sharing_max_days != null) setMaxDays(s.sharing_max_days);
            if (s.sharing_deposit != null) setDeposit(s.sharing_deposit);
        }
        toast.success(`KI-Empfehlung übernommen: ${t === "auction" ? "Auktion" : "Sharing"}`);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const body = { target };
            if (target === "auction") {
                body.starting_price = Number(startingPrice);
                if (buyNowPrice) body.buy_now_price = Number(buyNowPrice);
                body.auction_end = new Date(auctionEnd).toISOString();
            } else if (target === "sharing") {
                body.sharing_daily_rate = Number(dailyRate);
                body.sharing_min_days = Number(minDays);
                body.sharing_max_days = Number(maxDays);
                body.sharing_deposit = Number(deposit);
            }
            const r = await api.post(`/listings/${listing.id}/convert`, body);
            const TLABEL = { auction: "Auktion", sharing: "Sharing", fixed: "Festpreis" };
            toast.success(`Inserat in ${TLABEL[target]} überführt`);
            if (typeof onSuccess === "function") onSuccess(r.data.listing);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Konvertierung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="convert-listing-dialog">
            <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-[#FFCC00] rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-[#FFCC00]/10">
                    <div>
                        <h3 className="font-display text-lg font-black tracking-tight">Inserat umstellen</h3>
                        <div className="text-xs text-gray-600 truncate max-w-xs">{listing.title}</div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-[#111827] p-1" aria-label="Schließen" data-testid="convert-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {recommendation && recommendation.recommended_target && (
                        <div className="bg-gradient-to-br from-[#FFCC00]/30 to-[#FFCC00]/10 border-2 border-[#FFCC00] rounded-sm p-4" data-testid="convert-smart-recommendation">
                            <div className="flex items-start gap-2.5">
                                <Sparkles className="w-4 h-4 text-[#111827] mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-black text-sm tracking-tight">
                                        KI-Empfehlung: {recommendation.recommended_target === "auction" ? "Auktion" : "Sharing aktivieren"}
                                    </div>
                                    <p className="text-[12px] text-gray-800 mt-1 leading-relaxed">{recommendation.reason}</p>
                                    {recommendation.recommended_target === "auction" && recommendation.comparable_count > 0 && (
                                        <div className="text-[11px] text-gray-700 mt-1.5 inline-flex items-center gap-2">
                                            <span className="font-mono-num">{recommendation.comparable_count}</span> Vergleichsverkäufe ·
                                            Median <span className="font-mono-num font-bold">{Number(recommendation.comparable_median_price).toLocaleString("de-DE")} €</span> ·
                                            Empfohlener Startpreis <span className="font-mono-num font-bold">{Number(recommendation.suggested?.starting_price).toLocaleString("de-DE")} €</span>
                                        </div>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    onClick={applyRecommendation}
                                    size="sm"
                                    className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider text-xs shrink-0"
                                    data-testid="convert-apply-recommendation"
                                >
                                    Übernehmen
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2" data-testid="convert-target-selector">
                        {TARGETS
                            .filter((t) => !(t.role === "b2b" && !isDealer))
                            .filter((t) => t.key !== listing.sale_type || t.key === "sharing")
                            .map((t) => {
                                const Icon = t.icon;
                                const active = target === t.key;
                                return (
                                    <button
                                        type="button"
                                        key={t.key}
                                        onClick={() => setTarget(t.key)}
                                        className={`w-full text-left p-3 rounded-sm border-2 transition-colors flex items-start gap-3 ${active ? "border-[#FFCC00] bg-[#FFCC00]/15" : "border-gray-200 bg-white hover:border-gray-400"}`}
                                        data-testid={`convert-target-${t.key}`}
                                    >
                                        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${active ? "text-[#111827]" : "text-gray-500"}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-sm">{t.label}</div>
                                            <div className="text-xs text-gray-600 mt-0.5">{t.desc}</div>
                                        </div>
                                    </button>
                                );
                            })}
                    </div>

                    {target === "auction" && (
                        <div className="space-y-3 pt-3 border-t border-gray-100" data-testid="convert-auction-fields">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Startpreis (€) *</Label>
                                    <Input type="number" min="1" step="1" value={startingPrice} onChange={(e) => setStartingPrice(e.target.value)} className="rounded-sm font-mono-num" data-testid="convert-start-price" />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Sofort-Kauf-Preis (€)</Label>
                                    <Input type="number" min="1" step="1" value={buyNowPrice} onChange={(e) => setBuyNowPrice(e.target.value)} placeholder="optional" className="rounded-sm font-mono-num" data-testid="convert-buynow-price" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[11px] uppercase tracking-wider font-bold">Auktionsende *</Label>
                                <Input type="datetime-local" value={auctionEnd} onChange={(e) => setAuctionEnd(e.target.value)} className="rounded-sm" data-testid="convert-auction-end" />
                                <p className="text-[10px] text-gray-500 mt-1">Muss in der Zukunft liegen. Üblich sind 7–14 Tage.</p>
                            </div>
                        </div>
                    )}

                    {target === "sharing" && (
                        <div className="space-y-3 pt-3 border-t border-gray-100" data-testid="convert-sharing-fields">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Tagesrate (€) *</Label>
                                    <Input type="number" min="1" step="1" value={dailyRate} onChange={(e) => setDailyRate(e.target.value)} className="rounded-sm font-mono-num" data-testid="convert-daily-rate" />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Kaution (€)</Label>
                                    <Input type="number" min="0" step="10" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="rounded-sm font-mono-num" data-testid="convert-deposit" />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Min. Tage</Label>
                                    <Input type="number" min="1" max="365" value={minDays} onChange={(e) => setMinDays(e.target.value)} className="rounded-sm font-mono-num" data-testid="convert-min-days" />
                                </div>
                                <div>
                                    <Label className="text-[11px] uppercase tracking-wider font-bold">Max. Tage</Label>
                                    <Input type="number" min="1" max="365" value={maxDays} onChange={(e) => setMaxDays(e.target.value)} className="rounded-sm font-mono-num" data-testid="convert-max-days" />
                                </div>
                            </div>
                            <p className="text-[11px] text-gray-500">Sharing kann zusätzlich zum Festpreis aktiviert werden — das Inserat bleibt verkäuflich.</p>
                        </div>
                    )}

                    {target === "fixed" && (
                        <div className="pt-3 border-t border-gray-100 text-xs text-gray-700 bg-gray-50 p-3 rounded-sm" data-testid="convert-fixed-info">
                            Auktions-Daten (Startpreis, Sofortkauf, Auktionsende, Gebote-Historie wird beibehalten als Audit) und Sharing-Settings werden zurückgesetzt. Das Inserat bleibt mit dem aktuellen Festpreis öffentlich.
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-gray-200 bg-gray-50">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="convert-cancel">Abbrechen</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider"
                        data-testid="convert-submit"
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                        Jetzt umstellen
                    </Button>
                </div>
            </div>
        </div>
    );
}
